/**
 * Client-side helpers for the first-booking welcome discount.
 *
 * Eligibility is always resolved authoritatively on the server
 * (see server/api-util/firstBookingDiscount.js). These helpers only drive UI:
 * whether to show the offer checkbox, the listing-card badge and the booking
 * banner. Keep the values below in sync with the server copy.
 */

// Accepts a boolean from the API / Transit, plus string/number variants in case
// the flag arrives as 'true' / 1 / '1'.
export const isFirstBookingDiscountFlagOn = value =>
  value === true || value === 'true' || value === 1 || value === '1';

// Last transitions that mean the host has already received a real booking
// (paid/accepted/completed/reviewed). A decline, expiry or cancellation does
// NOT count, freeing the welcome offer again.
// NOTE: keep in sync with server/api-util/firstBookingDiscount.js
// BLOCKING_LAST_TRANSITIONS.
export const HOST_BOOKING_LAST_TRANSITIONS = [
  'transition/confirm-payment',
  'transition/accept',
  'transition/operator-accept',
  'transition/complete',
  'transition/operator-complete',
  'transition/review-1-by-provider',
  'transition/review-2-by-provider',
  'transition/review-1-by-customer',
  'transition/review-2-by-customer',
  'transition/expire-review-period',
  'transition/expire-customer-review-period',
  'transition/expire-provider-review-period',
];

/**
 * Whether to show first-booking discount UI (listing card badge, booking banner).
 * Eligibility is per host: the listing flag must be on and the host must not have
 * received a booking yet, unless an admin override is set on their profile.
 */
export const isFirstBookingDiscountEligible = (publicData, author) => {
  if (!isFirstBookingDiscountFlagOn(publicData?.firstBookingDiscountEnabled)) {
    return false;
  }
  const profilePublicData = author?.attributes?.profile?.publicData;
  const hostHasReceivedBooking = profilePublicData?.hasReceivedBooking === true;
  const hostHasDiscountOverride = profilePublicData?.firstBookingDiscountOverride === true;
  if (hostHasReceivedBooking && !hostHasDiscountOverride) {
    return false;
  }
  return true;
};
